import { defineComponent as _defineComponent } from 'vue'
import { Html5Qrcode } from 'html5-qrcode'
import { ref, watch, onBeforeUnmount } from 'vue'
import QRCodeScan from '@/assets/icons/QRCodeScan.vue'
import { ModalWrapper } from '@/shared/ui/modals'
import { mdiFlashlight, mdiFlashlightOff } from '@mdi/js'
import { useCheckInTicketMutation, useGetTicketByQRCodeMutation } from '@/features/tickets/api/queries'


export default /*#__PURE__*/_defineComponent({
  __name: 'QRCodeScanner',
  emits: ["decode"],
  setup(__props, { emit }: { emit: ({
  (e: 'decode', value: string): void
}), expose: any, slots: any, attrs: any }) {



const getTicketByQRCodeMutation = useGetTicketByQRCodeMutation()
const checkInTicketMutation = useCheckInTicketMutation()

const showScanner = ref(false)
const errorMessage = ref<string | null>(null)
const alreadyUsedMessage = ref<string | null>(null)
const cameraReady = ref(false)
const hasTorch = ref(false)
const torchEnabled = ref(false)
const hasCamera = ref(true)

let html5QrCode: Html5Qrcode | null = null
const scannerId = 'qr-scanner-region'

const stopScanner = async () => {
  if (html5QrCode) {
    try {
      if (html5QrCode.isScanning) {
        await html5QrCode.stop()
      }
    } catch (error) {
      console.error('Error stopping scanner:', error)
    }
    html5QrCode = null
  }
  cameraReady.value = false
  hasTorch.value = false
  torchEnabled.value = false
  alreadyUsedMessage.value = null
}

const onScanSuccess = (decodedText: string) => {
  if (getTicketByQRCodeMutation.isPending.value || checkInTicketMutation.isPending.value) {
    return
  }

  getTicketByQRCodeMutation.mutate(decodedText, {
    onSuccess(data) {
      if (data.isCheckedIn) {
        alreadyUsedMessage.value = 'This ticket has already been used.'
        return
      }

      checkInTicketMutation.mutate(data.id, {
        onError() {
          emit('decode', decodedText)
          stopScanner()
          showScanner.value = false
        },
        onSuccess() {
          emit('decode', decodedText)
          stopScanner()
          showScanner.value = false
        },
      })
    },
  })
}

const onScanError = (errorMsg: string) => {
  // Ignore "No QR code found" errors as they happen continuously while scanning
  if (!errorMsg.includes('No barcode or QR code detected')) {
    console.debug('QR scan error:', errorMsg)
  }
}

const startScanner = async () => {
  try {
    const devices = await Html5Qrcode.getCameras()
    if (!devices || devices.length === 0) {
      hasCamera.value = false
      errorMessage.value = 'No camera devices found. Please ensure you have a camera connected.'
      return
    }

    hasCamera.value = true
    html5QrCode = new Html5Qrcode(scannerId)

    // Prefer back camera for mobile devices
    const backCamera = devices.find(
      (device) => device.label.toLowerCase().includes('back') || device.label.toLowerCase().includes('rear')
    )
    const cameraId = backCamera?.id || devices[0].id

    await html5QrCode.start(
      cameraId,
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      },
      onScanSuccess,
      onScanError
    )

    cameraReady.value = true
    errorMessage.value = null

    // Check if torch is available
    const capabilities = html5QrCode.getRunningTrackCameraCapabilities()
    if (capabilities?.torchFeature()?.isSupported()) {
      hasTorch.value = true
    }
  } catch (error) {
    const err = error as Error
    cameraReady.value = false

    if (err.message?.includes('Permission')) {
      errorMessage.value = 'Camera access permission denied. Please allow camera access to scan QR codes.'
    } else if (err.message?.includes('NotFoundError')) {
      errorMessage.value = 'No suitable camera device found. Please ensure you have a camera connected.'
    } else if (err.message?.includes('NotReadableError')) {
      errorMessage.value =
        'Camera is already in use by another application. Please close other applications using the camera.'
    } else {
      errorMessage.value = `Camera error: ${err.message}`
    }
  }
}

const toggleTorch = async () => {
  if (!html5QrCode || !cameraReady.value) return

  try {
    const capabilities = html5QrCode.getRunningTrackCameraCapabilities()
    const torchFeature = capabilities?.torchFeature()
    if (torchFeature?.isSupported()) {
      await torchFeature.apply(!torchEnabled.value)
      torchEnabled.value = !torchEnabled.value
    }
  } catch (error) {
    console.error('Error toggling torch:', error)
  }
}

watch(showScanner, async (isVisible) => {
  if (isVisible) {
    // Wait for the DOM to be ready
    await new Promise((resolve) => {
      setTimeout(resolve, 100)
    })
    await startScanner()
  } else {
    await stopScanner()
  }
})

onBeforeUnmount(() => {
  stopScanner()
})

return { __sfc: true,emit, getTicketByQRCodeMutation, checkInTicketMutation, showScanner, errorMessage, alreadyUsedMessage, cameraReady, hasTorch, torchEnabled, hasCamera, html5QrCode, scannerId, stopScanner, onScanSuccess, onScanError, startScanner, toggleTorch, QRCodeScan, ModalWrapper, mdiFlashlight, mdiFlashlightOff }
}

})